import './GameModal.scss';

import * as Yup from 'yup';

import { Button, Form, Modal, ModalBody } from 'react-bootstrap';
import { IGDB, gameExperiences } from '../../utils/constants';
import {
  checkUserStatus,
  deleteCandidateGame,
  getCandidateGameExperiencesPlayed,
  postCandidateGame,
  putCandidateGame,
  selectIsDeletingCandidateGame
} from '../../ducks/entities';
import { useDispatch, useSelector } from 'react-redux';

import { Formik } from 'formik';
import { Game } from '../../models/game';
import { GameExperience } from '../../models/survey';
import LoaderComponent from '../Loader/LoaderComponents';
import SelectQuestion from '../SelectQuestion/SelectQuestion';
import YesNoButton from '../YesNoButton/YesNoButton';
import { createLocalizedOptions } from '../../pages/root/App';
import { getGameDisplayString } from '../../utils/generic';
import iconAnimation from '../../assets/images/ubisoft-playtest-profile-icon-person-running-black.webp';
import iconCollaboration from '../../assets/images/ubisoft-playtest-profile-icon-group-people-black.webp';
import iconFlag from '../../assets/images/ubisoft-playtest-profile-icon-flag-black.webp';
import iconGarbage from '../../assets/images/ubisoft-playtest-icon-garbage-red.webp';
import iconSmiley from '../../assets/images/ubisoft-playtest-profile-icon-smiley-black-with-padding.webp';
import iconSupport from '../../assets/images/ubisoft-playtest-profile-icon-hands-holding-black.webp';
import messages from './messages';
import noImage from '../../assets/images/ubisoft-playtest-icon-game-no-picture.webp';
import siteMessages from '../../pages/root/siteMessages';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';
import { useState } from 'react';

interface OwnProps {
  game?: Game;
  gameExperience?: GameExperience;
  onBack?: () => void;
  onClose: () => void;
  submit?: boolean;
  onChangeValues?: (values: any) => void;
  onDelete?: (id: string) => void;
}

const GameModal = (props: OwnProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const isDeleting = useSelector(selectIsDeletingCandidateGame);
  const [validateOnChange, setValidateOnChange] = useState(false);

  const experienceOptions = createLocalizedOptions(gameExperiences, messages);

  let baseSchema = {
    liked: Yup.boolean().required(
      intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
    ),
    finished: Yup.boolean().required(
      intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
    )
  };

  const singlePlayerSchema = {
    singleExperienceId: Yup.number().required(
      intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
    )
  };

  const multiPlayerSchema = {
    multiExperienceId: Yup.number().required(
      intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
    )
  };

  const coopSchema = {
    coopExperienceId: Yup.number().required(
      intl.formatMessage({ ...siteMessages.FIELD_REQUIRED })
    )
  };

  if (props.game) {
    if (props.game.singlePlayerMode)
      baseSchema = { ...baseSchema, ...singlePlayerSchema };
    if (props.game.coopMode) baseSchema = { ...baseSchema, ...coopSchema };
    if (props.game.multiplayerMode)
      baseSchema = { ...baseSchema, ...multiPlayerSchema };
  } else if (props.gameExperience) {
    if (props.gameExperience.game.singlePlayerMode)
      baseSchema = { ...baseSchema, ...singlePlayerSchema };
    if (props.gameExperience.game.coopMode)
      baseSchema = { ...baseSchema, ...coopSchema };
    if (props.gameExperience.game.multiplayerMode)
      baseSchema = { ...baseSchema, ...multiPlayerSchema };
  }

  const schema = Yup.object().shape({
    ...baseSchema
  });

  const onSubmit = async (values: GameExperience, { setSubmitting }: any) => {
    setSubmitting(true);
    if (props.game) {
      await (
        dispatch(
          postCandidateGame({ ...values, igdbGameId: parseInt(props.game.id) })
        ) as any
      )
        .then(() => {
          toast.success(
            intl.formatMessage({ ...siteMessages.updateProfileSuccess })
          );
          setSubmitting(false);
          props.onClose();
          dispatch(getCandidateGameExperiencesPlayed());
          dispatch(checkUserStatus());
        })
        .catch(() =>
          toast.error(intl.formatMessage({ ...siteMessages.updateProfileFail }))
        );
    } else if (props.gameExperience) {
      await (
        dispatch(
          putCandidateGame(
            {
              ...values,
              singleExperienceId: props.gameExperience.game.singlePlayerMode
                ? values.singleExperienceId
                : undefined,
              multiExperienceId: props.gameExperience.game.multiplayerMode
                ? values.multiExperienceId
                : undefined,
              coopExperienceId: props.gameExperience.game.coopMode
                ? values.coopExperienceId
                : undefined
            },
            props.gameExperience.id
          )
        ) as any
      )
        .then(() => {
          toast.success(
            intl.formatMessage({ ...siteMessages.updateProfileSuccess })
          );
          setSubmitting(false);
          props.onClose();
          dispatch(getCandidateGameExperiencesPlayed());
          dispatch(checkUserStatus());
        })
        .catch(() =>
          toast.error(intl.formatMessage({ ...siteMessages.updateProfileFail }))
        );
    }
  };

  const onDelete = async () => {
    if (!props.gameExperience) return;
    await (dispatch(deleteCandidateGame(props.gameExperience.id)) as any)
      .then(() => {
        toast.success(
          intl.formatMessage({ ...siteMessages.updateProfileSuccess })
        );
        props.onClose();
        dispatch(getCandidateGameExperiencesPlayed());
        dispatch(checkUserStatus());
      })
      .catch(() =>
        toast.error(intl.formatMessage({ ...siteMessages.updateProfileFail }))
      );
  };

  const onSave = (values: GameExperience, { setSubmitting }: any) => {
    props.onChangeValues && props.onChangeValues(values);
    setSubmitting(false);
    props.onClose();
  };

  const onRemove = () => {
    props.onDelete &&
      props.gameExperience &&
      props.onDelete(props.gameExperience.id);
    props.onClose();
  };

  const displayName = () => {
    if (props.game?.name) {
      return getGameDisplayString(props.game);
    } else if (props.gameExperience) {
      return getGameDisplayString(props.gameExperience);
    }
  };

  return (
    <Modal
      className="game-modal"
      show
      onHide={props.onBack ?? props.onClose}
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton />
      <ModalBody>
        <Formik
          initialValues={props.gameExperience || ({} as any)}
          validationSchema={schema}
          onSubmit={props.submit ? onSubmit : onSave}
          validateOnChange={validateOnChange}
        >
          {({ values, errors, handleSubmit, isSubmitting, setFieldValue }) => (
            <>
              <div className="header">
                <img
                  className="me-2"
                  alt={
                    props.game?.name
                      ? props.game.name
                      : props.gameExperience?.game?.name
                  }
                  src={`${IGDB.baseURL}${
                    props.game?.imageId
                      ? props.game.imageId
                      : props.gameExperience?.game?.imageId
                  }`}
                  onError={(e: any) => {
                    e.target.src = noImage;
                  }}
                />
                <h3>{displayName()}</h3>
                {!props.game && (
                  <Button
                    className="delete"
                    disabled={isDeleting}
                    variant="link"
                    onClick={props.submit ? onDelete : onRemove}
                  >
                    <LoaderComponent
                      isLoading={isSubmitting}
                      className="lds-small"
                    >
                      <img src={iconGarbage} alt="" />
                    </LoaderComponent>
                  </Button>
                )}
              </div>
              <div className="content">
                <Form noValidate onSubmit={handleSubmit}>
                  {(props.game?.singlePlayerMode ||
                    props.gameExperience?.game.singlePlayerMode ||
                    props.game?.multiplayerMode ||
                    props.gameExperience?.game.multiplayerMode ||
                    props.game?.coopMode ||
                    props.gameExperience?.game.coopMode) && (
                    <Form.Label className="experience-label">
                      {intl.formatMessage({ ...messages.howManyHours })}
                    </Form.Label>
                  )}
                  <div className="section">
                    {(props.game?.singlePlayerMode ||
                      props.gameExperience?.game.singlePlayerMode) && (
                      <div className="question-select">
                        <img src={iconAnimation} alt="" />
                        <SelectQuestion
                          controlId="singleExperienceId"
                          label={intl.formatMessage({
                            ...messages.singlePlayer
                          })}
                          isMulti={false}
                          options={experienceOptions}
                          value={values.singleExperienceId}
                          error={errors.singleExperienceId}
                          setFieldValue={setFieldValue}
                        />
                      </div>
                    )}
                    {(props.game?.multiplayerMode ||
                      props.gameExperience?.game.multiplayerMode) && (
                      <div className="question-select">
                        <img src={iconCollaboration} alt="" />
                        <SelectQuestion
                          controlId="multiExperienceId"
                          label={intl.formatMessage({
                            ...messages.multiPlayer
                          })}
                          isMulti={false}
                          options={experienceOptions}
                          value={values.multiExperienceId}
                          error={errors.multiExperienceId}
                          setFieldValue={setFieldValue}
                        />
                      </div>
                    )}
                    {(props.game?.coopMode ||
                      props.gameExperience?.game.coopMode) && (
                      <div className="question-select">
                        <img src={iconSupport} alt="" />
                        <SelectQuestion
                          controlId="coopExperienceId"
                          label={intl.formatMessage({ ...messages.coop })}
                          isMulti={false}
                          options={experienceOptions}
                          value={values.coopExperienceId}
                          error={errors.coopExperienceId}
                          setFieldValue={setFieldValue}
                        />
                      </div>
                    )}
                  </div>
                  {(props.game?.singlePlayerMode ||
                    props.gameExperience?.game?.singlePlayerMode ||
                    props.game?.multiplayerMode ||
                    props.gameExperience?.game?.multiplayerMode ||
                    props.game?.coopMode ||
                    props.gameExperience?.game?.coopMode) && (
                    <hr className="divider" />
                  )}
                  <div className="section">
                    <div className="question-yesno">
                      <img src={iconSmiley} alt="" className="mt-n1" />
                      <YesNoButton
                        id="liked"
                        name="liked"
                        label={intl.formatMessage({ ...messages.likeGame })}
                        value={values.liked ?? null}
                        onChange={(value: boolean) =>
                          setFieldValue('liked', value)
                        }
                        error={errors.liked}
                      />
                    </div>
                    <div className="question-yesno">
                      <img src={iconFlag} alt="" className="mt-n1" />
                      <YesNoButton
                        id="finished"
                        name="finished"
                        label={intl.formatMessage({ ...messages.finishGame })}
                        value={values.finished ?? null}
                        onChange={(value: boolean) =>
                          setFieldValue('finished', value)
                        }
                        error={errors.finished}
                      />
                    </div>
                  </div>
                  <div className="btns">
                    <Button
                      className="mx-1"
                      variant="secondary"
                      disabled={isSubmitting}
                      onClick={props.onBack ?? props.onClose}
                    >
                      {props.onBack && props.game
                        ? intl.formatMessage({ ...siteMessages.back })
                        : intl.formatMessage({ ...siteMessages.cancel })}
                    </Button>
                    <Button
                      className="mx-1"
                      variant="primary"
                      disabled={isSubmitting}
                      onClick={() => {
                        setValidateOnChange(true);
                        handleSubmit();
                      }}
                    >
                      <LoaderComponent
                        isLoading={isSubmitting}
                        className="lds-small"
                      >
                        {intl.formatMessage({ ...siteMessages.save })}
                      </LoaderComponent>
                    </Button>
                  </div>
                </Form>
              </div>
            </>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  );
};

export default GameModal;
